import { Component, Input, OnInit } from '@angular/core';
import {
  EmployeePay,
  MasterPayrollObject,
  Payline,
  PaylineType,
  PayrollConfigSetupData,
  PayTypes
} from '../../../../models/payroll';
import { PayrollFrequency } from '../../../../models/payroll-info';
import { CompanyDeductionConfig } from '../../../../models/company-deduction-config';
import { CompanyEarningConfig } from '../../../../models/company-earning-config';
import { PayrollManagement } from '../../../../tools/payroll-management';
import { BasePayrollComponent } from '../../../../components/public-components';
import { Division } from '../../../../models/division';

@Component({
  selector: 'payline-detail-table',
  templateUrl: './payline-detail-table.component.html',
  styleUrls: ['./payline-detail-table.component.css'],
  standalone: false
})
export class PaylineDetailTableComponent extends BasePayrollComponent implements OnInit {
  @Input() clientCode: string;
  @Input() configSetupData: PayrollConfigSetupData;
  @Input() pay: EmployeePay;
  @Input() payroll: MasterPayrollObject;
  @Input() divisions: Division[];

  public loaded: boolean = false;
  // paylines that are ok to pay
  // with earnings/deductions/taxes
  // filtered by only those that have
  // either current or YTD amounts
  public paylines: Payline[] = [];
  public readonly paylineType: typeof PaylineType = PaylineType;

  get payrollFrequencies() {
    return PayrollFrequency;
  }

  get payTypes() {
    return PayTypes;
  }

  constructor(public payrollMgmt: PayrollManagement) {
    super();
  }

  ngOnInit(): void {
    this.paylines = this._filterPaylines();

    if (!this.paylines.filter(x => x.OkToPay).length) {
      return; // do not render anything
    }

    this._sortDeductions();
    this._sortEarnings();
    this.loaded = true;
  }

  public getDeductionName(deductionId: string) {
    return this._getDeductionConfig(deductionId)?.Name;
  }

  public getEarningName(earningId: string) {
    return this._getEarningConfig(earningId)?.Name;
  }

  public getDivisionName(division: number) {
    return this.divisions?.length
      ? (this.divisions.find(d => d.Id == division)?.DisplayId ?? division)
      : '';
  }

  public getDepartmentName(divisionId: number, departmentId: number) {
    var div = this.divisions?.find(d => d.Id == divisionId);
    if (div == null) {
      return departmentId;
    }

    return div.Departments.find(dept => dept.Id == departmentId)?.DisplayId;
  }

  // #region private methods

  private _getDeductionConfig(deductionId): CompanyDeductionConfig {
    let config;

    this.configSetupData.Deductions.forEach(ded => {
      if (ded.Id == deductionId) {
        config = ded;
      }
    });

    return config;
  }

  private _getEarningConfig(earningId): CompanyEarningConfig {
    let config;

    this.configSetupData.Earnings.forEach(earn => {
      if (earn.Id == earningId) {
        config = earn;
      }
    });

    return config;
  }

  private _filterPaylines() {
    const paylines = [];

    this.pay.Paylines.forEach(pay => {
      if (!pay.OkToPay) {
        return;
      }

      let line = { ...pay };

      line.Earnings = pay.Earnings.filter(x => {
        return x.Hours || x.YTDHours || x.CurrentAmount || x.YTDAmount;
      });

      line.Deductions = pay.Deductions.filter(x => {
        return x.CurrentAmount || x.YTDAmount;
      });

      line.Taxes = pay.Taxes.filter(x => {
        // only display taxes paid by the employee
        return x.TaxPaidBy === 'E' && (x.CurrentAmount || x.YTDAmount);
      });

      paylines.push(line);
    });

    return paylines;
  }

  private _sortDeductions() {
    this.paylines.forEach(line => {
      line.Deductions.sort((a, b) => {
        let left = this._getDeductionConfig(a.DeductionCode);
        let right = this._getDeductionConfig(b.DeductionCode);

        return left.AppearanceSequence - right.AppearanceSequence;
      });
    });
  }

  private _sortEarnings() {
    this.paylines.forEach(line => {
      line.Earnings.sort((a, b) => {
        let left = this._getEarningConfig(a.EarningId);
        let right = this._getEarningConfig(b.EarningId);

        return left.AppearanceSequence - right.AppearanceSequence;
      });
    });
  }
  // #endregion
}
